import { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  MapPin,
  DollarSign,
  MessageCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  User,
  Phone,
  Coffee,
  UserPlus,
  Plus,
  AlertCircle,
  Flag,
  Send,
  Navigation,
  QrCode,
  Briefcase,
  Award,
  Eye,
  EyeOff
} from "lucide-react";

import EmptyState from "../EmptyState/EmptyState";
import { formatDatePtBr } from "../../utils/dateUtils";

import {
  getCompanyInProgressJobs,
  getFreelancerInProgressJobs,
  earlyExitJob,
  markOnTheWay,
  confirmArrival,
  reportNoShow,
  extendTolerance,
  invalidateApplication,
} from "../../services/inprogressService";

import { connectToChat, getConversations } from "../../services/chatService";

import EarlyExitModal from "../Earlyexitmodal/Earlyexitmodal";
import AutoFinishedModal from "../Autofinishedmodal/Autofinishedmodal";
import ProgressTimeline from "./ProgressTimeline";
import CompanyQRCode from "./CompanyQRCode";
import FreelancerScanner from "../SharedInProgress/FreelancerScanner/FreelancerScanner";
import PresenceAlert from "./PresenceAlert/PresenceAlert";
import AddExtraHoursModal from "../AddExtraHoursModal/AddExtraHoursModal";
import EvaluationModal from "../EvaluationModal/EvaluationModal";
import InvalidateApplicationModal from "../InvalidateApplicationModal/InvalidateApplicationModal";
import ChatModal from "../Chat/ChatModal/ChatModal";
import TransferSuccessModal from "../TransferSucessModal/TransferSucessModal";
import Toast from "../Toast/Toast";
import "./InProgress.scss";


// ==========================================
// UTILITÁRIOS
// ==========================================
function formatTime(t) {
  return t ? t.slice(0, 5) : "—";
}

// ==========================================
// SUBCOMPONENTES
// ==========================================

// Avatar com fallback
function AvatarFallback({ name, src, size = 40 }) {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="ip-avatar"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="ip-avatar-fallback"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {name?.slice(0, 2).toUpperCase()}
    </div>
  );
}

// Info item com ícone
function InfoItem({ icon: Icon, label, value, highlight = false }) {
  return (
    <div className="ip-card__info-item">
      <Icon size={18} className="ip-card__info-icon" />
      <div>
        <p className="ip-card__info-label">{label}</p>
        <p className={`ip-card__info-value ${highlight ? 'ip-card__info-value--highlight' : ''}`}>
          {value || "—"}
        </p>
      </div>
    </div>
  );
}

// Badge de status
function StatusBadge({ status }) {
  const configs = {
    waiting: { label: "Aguardando", icon: Clock, color: "warning" },
    on_way: { label: "A caminho", icon: Navigation, color: "info" },
    arrived: { label: "Chegou", icon: CheckCircle, color: "success" },
  };

  const config = configs[status] || configs.waiting;
  const Icon = config.icon;

  return (
    <span className={`ip-badge ip-badge--${config.color}`}>
      <Icon size={14} />
      {config.label}
    </span>
  );
}

// ==========================================
// CARD PRINCIPAL
// ==========================================
function InProgressCard({
  item,
  userType,
  onEarlyExit,
  onUpdateStatus,
  onExtraHoursAdded,
  onConversationCreated,
  showToast
}) {
  const job = item.job || item;
  const freelancer = item.freelancer || item.user;
  const [expanded, setExpanded] = useState(false);
  const [showEarlyExit, setShowEarlyExit] = useState(false);
  const [showExtraHours, setShowExtraHours] = useState(false);
  const [showInvalidateModal, setShowInvalidateModal] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatConversationId, setChatConversationId] = useState(null);
  const [scannerRef, setScannerRef] = useState(null);
  const [exitMode, setExitMode] = useState("normal");
  const [showTransferSuccess, setShowTransferSuccess] = useState(false);
  const [transferData, setTransferData] = useState(null);

  // ─── Status do turno ──────────────────────────────────────────────────
  const getTimelineStatus = (application) => {
    if (application.status === "completed" || application.status === "early_exit")
      return "finished";
    if (application.isArrived) return "arrived";
    if (application.isOnTheWay) return "on_the_way";
    return "departed";
  };

  const currentTimelineStatus = getTimelineStatus(item);

  // ─── Configuração do status atual ────────────────────────────────────
  const getStatusConfig = (status, userType) => {
    const isFreelancer = userType === "Freelancer";
    const isCompany = userType === "Company";

    const configs = {
      departed: {
        icon: Navigation,
        color: "#3b82f6",
        bgColor: "#eff6ff",
        freelancerMessage: "🚶 Você ainda não iniciou seu trajeto. Avise a empresa que está a caminho.",
        companyMessage: "⏳ Aguardando o freelancer confirmar que iniciou o trajeto.",
        action: isFreelancer ? "Estou a caminho" : null,
        actionIcon: isFreelancer ? Navigation : null
      },
      on_the_way: {
        icon: Navigation,
        color: "#f59e0b",
        bgColor: "#fffbeb",
        freelancerMessage: "🚶 Você está a caminho da empresa. Ao chegar, escaneie o QR Code.",
        companyMessage: "🚶 O freelancer está a caminho. Aguarde a confirmação de chegada.",
        action: isFreelancer ? "Ver QR Code" : (isCompany ? "Ver QR Code" : null),
        actionIcon: isFreelancer ? QrCode : (isCompany ? QrCode : null)
      },
      arrived: {
        icon: CheckCircle,
        color: "#22c55e",
        bgColor: "#f0fdf4",
        freelancerMessage: "✅ Você chegou ao local! Aguarde a empresa finalizar o turno.",
        companyMessage: "✅ O freelancer chegou ao local. Clique em Finalizar para encerrar.",
        action: isFreelancer ? null : "Finalizar turno",
        actionIcon: isFreelancer ? null : CheckCircle
      },
      finished: {
        icon: CheckCircle,
        color: "#6b7280",
        bgColor: "#f3f4f6",
        freelancerMessage: "✅ Turno finalizado. Obrigado pelo trabalho!",
        companyMessage: "✅ Vaga finalizada. Obrigado!",
        action: null,
        actionIcon: null
      }
    };
    return configs[status] || configs.departed;
  };

  const statusConfig = getStatusConfig(currentTimelineStatus, userType);
  const StatusIcon = statusConfig.icon;
  const isFreelancer = userType === "Freelancer";

  // ─── Ações ─────────────────────────────────────────────────────────────
  const handlePrimaryAction = async () => {
    if (currentTimelineStatus === "departed") {
      // Somente freelancer pode iniciar o trajeto
      if (userType !== "Freelancer") {
        return;
      }

      try {
        await markOnTheWay(item.id);
        onUpdateStatus(item.id, "isOnTheWay");
      } catch (error) {
        console.error("Erro ao marcar como a caminho:", error);
        showToast(
          "Não foi possível registrar o início do trajeto. Tente novamente.",
          "error"
        );
      }

      return;
    }

    if (currentTimelineStatus === "on_the_way") {
      // Freelancer escaneia o QR Code
      if (userType === "Freelancer") {
        handleOpenScanner();
        return;
      }

      // Empresa vê o QR Code
      if (userType === "Company") {
        setExpanded(!expanded);
        return;
      }
    }

    if (currentTimelineStatus === "arrived") {
      // Somente empresa pode finalizar
      if (userType === "Company") {
        openExitModal("normal");
        return;
      }

      showToast("Aguardando a empresa finalizar o turno.", "info");
      return;
    }
  };

  const openExitModal = (mode) => {
    setExitMode(mode);
    setShowEarlyExit(true);
  };

  // Abre o scanner e expande o card
  const handleOpenScanner = () => {
    // Se não estiver expandido, expande primeiro
    if (!expanded) {
      setExpanded(true);
      // Aguarda a renderização e depois scrolla
      setTimeout(() => {
        // Tenta encontrar o scanner por ref ou por ID
        const scannerElement = scannerRef || document.getElementById('scanner-section');
        if (scannerElement) {
          scannerElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500); // Aumentei o delay para garantir a renderização
    } else {
      // Se já estiver expandido, só scrolla até o scanner
      setTimeout(() => {
        const scannerElement = scannerRef || document.getElementById('scanner-section');
        if (scannerElement) {
          scannerElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const handleConfirmArrival = async (code) => {
    try {
      await confirmArrival(item.id, code);
      onUpdateStatus(item.id, "isArrived");
      showToast("Chegada confirmada com sucesso.", "success");
    } catch (error) {
      showToast(
        error?.response?.data?.message || "Não foi possível confirmar a chegada.",
        "error"
      );
      throw error;
    }
  };

  const handleEarlyExitConfirm = async ({
    finishedOnTime,
    reason,
    freelancerPayment,
  }) => {
    try {
      await onEarlyExit(item, {
        finishedOnTime,
        reason,
        freelancerPayment,
      });

      setShowEarlyExit(false);
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        "Não foi possível finalizar a vaga.",
        "error"
      );
    }
  };

  const handleExtendTolerance = async (applicationId) => {
    try {
      await extendTolerance(applicationId);
      const newToleranceUntil = new Date(new Date().getTime() + 30 * 60000);
      onUpdateStatus(applicationId, "toleranceExtended", {
        toleranceUntil: newToleranceUntil.toISOString(),
      });
      showToast("Tolerância estendida com sucesso", "success");
    } catch (err) {
      showToast(
        "Não foi possível estender a tolerância. Tente novamente.",
        "error"
      );
    }
  };
  const handleReportNoShow = async (applicationId) => {
    if (!window.confirm("Deseja realmente invalidar esta candidatura? A vaga será reaberta como URGENTE."))
      return;
    try {
      await reportNoShow(applicationId);
      showToast(
        "A falta foi registrada e a vaga foi reaberta como urgente.",
        "success"
      );
      onUpdateStatus(applicationId, "isNoShow");
    } catch (err) {
      showToast(
        "Erro ao reportar falta, tente novamente daqui alguns minutos.",
        "error"
      );
    }
  };

  const handleInvalidate = () => {
    setShowInvalidateModal(true);
  };

  const handleConfirmInvalidate = async (bonusDescription) => {
    try {
      await invalidateApplication(item.id, bonusDescription);
      showToast(
        bonusDescription
          ? "Vaga reaberta como URGENTE com bônus."
          : "Vaga reaberta como URGENTE.",
        "success"
      );
      setShowInvalidateModal(false);
    } catch (err) {
      // console.log(err);
      showToast(
        "Erro ao invalidar candidatura. Tente novamente.",
        "error"
      );
    }
  };
  // ─── Handlers do Chat ─────────────────────────────────────────────────
  const handleOpenChat = () => {
    if (item.chatConversationId) {
      setChatConversationId(item.chatConversationId);
    }
    setShowChat(true);
  };

  const handleCloseChat = () => {
    setShowChat(false);
    setChatConversationId(null);
  };

  const userId = localStorage.getItem("userId");

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <>
      <div className={`ip-card ${expanded ? "ip-card--expanded" : ""}`}>
        {/* ─── HEADER ─────────────────────────────────────────────────── */}
        <div className="ip-card__header">
          <div className="ip-card__title-row">
            <div className="ip-card__title-group">
              <span className="ip-card__badge">
                <Briefcase size={12} />
                Em andamento
              </span>
              <h3 className="ip-card__title">{job.title}</h3>
            </div>
            <span className="ip-card__time">
              <Clock size={14} />
              {formatTime(job.startTime)} - {formatTime(job.endTime)}
            </span>
          </div>
        </div>
        {/* Presence Alert */}
        <PresenceAlert
          application={item}
          userType={userType}
          onExtendTolerance={() => handleExtendTolerance(item.id)}
          onReportNoShow={() => handleReportNoShow(item.id)}
          onInvalidateApplication={handleInvalidate}
        />

        {/* ─── STATUS DO TURNO (ALERTA PRINCIPAL) ───────────────────── */}
        <div className={`ip-card__status-alert ip-card__status-alert--${currentTimelineStatus}`}>
          <div className="ip-card__status-alert-icon">
            <StatusIcon size={20} />
          </div>
          <div className="ip-card__status-alert-content">
            <span className="ip-card__status-alert-label">Status do turno</span>
            <p className="ip-card__status-alert-message">
              {isFreelancer ? statusConfig.freelancerMessage : statusConfig.companyMessage}
            </p>
          </div>
          <button
            className="ip-card__status-alert-toggle"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <>
                <EyeOff size={16} />
                Ocultar
              </>
            ) : (
              <>
                <Eye size={16} />
                Ver acompanhamento
              </>
            )}
          </button>
        </div>

        {/* ─── CONTEÚDO EXPANDIDO ────────────────────────────────────── */}
        {expanded && (
          <div className="ip-card__expanded">
            {/* Timeline */}
            <div className="ip-card__timeline-section">
              <ProgressTimeline
                status={currentTimelineStatus}
                userType={userType}
                job={job}
                onPrimaryAction={handlePrimaryAction}
                onSecondaryAction={() => { }}
              />
            </div>

            {/* Detalhes adicionais */}
            <div className="ip-card__details-grid">
              {job.contactPerson && (
                <div className="ip-card__detail-item">
                  <User size={14} />
                  <span>Responsável</span>
                  <strong>{job.contactPerson}</strong>
                </div>
              )}

              {job.dressCode && (
                <div className="ip-card__detail-item">
                  <Award size={14} />
                  <span>Vestimenta</span>
                  <strong>{job.dressCode}</strong>
                </div>
              )}

              {job.breakTime && (
                <div className="ip-card__detail-item">
                  <Coffee size={14} />
                  <span>Intervalo</span>
                  <strong>{job.breakTime} min</strong>
                </div>
              )}

              {job.description && (
                <div className="ip-card__detail-item ip-card__detail-item--full">
                  <p className="ip-card__detail-label">Descrição</p>
                  <p className="ip-card__detail-text">{job.description}</p>
                </div>
              )}
            </div>

            {/* QR Code Scanner */}
            {isFreelancer && currentTimelineStatus === "on_the_way" && (
              <div
                ref={(el) => setScannerRef(el)}
                className="ip-card__scanner-section"
                id="scanner-section"
              >
                <FreelancerScanner onConfirmCode={handleConfirmArrival} />
              </div>
            )}

            {userType === "Company" && currentTimelineStatus === "on_the_way" && (
              <div className="ip-card__scanner-section">
                <CompanyQRCode presenceCode={item.presenceCode} />
              </div>
            )}

            {/* Freelancer info para empresa */}
            {userType === "Company" && freelancer && (
              <div className="ip-card__freelancer-section">
                <p className="ip-card__section-label">
                  <User size={14} />
                  Profissional alocado
                </p>
                <div className="ip-card__freelancer-row">
                  <AvatarFallback
                    name={freelancer.nomeSocial || freelancer.name}
                    src={freelancer.avatarUrl}
                    size={36}
                  />
                  <div>
                    <p className="ip-card__freelancer-name">{freelancer.nomeSocial || freelancer.name}</p>
                    {freelancer.phone && (
                      <p className="ip-card__freelancer-phone">
                        <Phone size={12} />
                        {freelancer.phone}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── FOOTER ─────────────────────────────────────────────────── */}
        <div className="ip-card__footer">
          <button
            className="ip-card__btn ip-card__btn--chat"
            onClick={handleOpenChat}
          >
            <div className="ip-card__chat-icon-wrapper">
              <MessageCircle size={16} />
              {item.unreadMessages > 0 && (
                <span className="ip-card__chat-dot" />
              )}
            </div>
            Chat
            {item.unreadMessages > 0 && (
              <span className="ip-card__chat-badge">{item.unreadMessages}</span>
            )}
          </button>

          <div className="ip-card__footer-actions">
            {statusConfig.action && (
              <button
                className="ip-card__btn ip-card__btn--primary"
                onClick={handlePrimaryAction}
              >
                <statusConfig.actionIcon size={16} />
                {statusConfig.action}
              </button>
            )}

            {userType === "Company" && currentTimelineStatus === "arrived" && (
              <button
                className="ip-card__btn ip-card__btn--early-exit"
                onClick={() => openExitModal("early")}
              >
                <AlertCircle size={16} />
                Encerrar antecipadamente
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ─── Modais ──────────────────────────────────────────────────────── */}
      {showEarlyExit && (
        <EarlyExitModal
          jobTitle={job.title}
          jobPayment={job.payment}
          mode={exitMode}
          onConfirm={handleEarlyExitConfirm}
          onClose={() => setShowEarlyExit(false)}
        />
      )}

      {showExtraHours && (
        <AddExtraHoursModal
          job={job}
          jobId={job.id}
          onClose={() => setShowExtraHours(false)}
          onConfirm={(newEndTime) => onExtraHoursAdded(item.id, newEndTime)}
        />
      )}

      {showInvalidateModal && (
        <InvalidateApplicationModal
          onConfirm={handleConfirmInvalidate}
          onClose={() => setShowInvalidateModal(false)}
        />
      )}

      {showChat && (
        <ChatModal
          isOpen={showChat}
          onClose={handleCloseChat}
          type="job"
          userId={userId}
          userType={userType}
          initialConversationId={chatConversationId || item.chatConversationId}
          jobId={job.id}
          freelancerId={userType === "Freelancer" ? userId : freelancer?.id}
          companyId={userType === "Company" ? userId : job.companyId}
          onConversationCreated={(conversationId) => {
            setChatConversationId(conversationId);
            //  Notifica o pai
            if (onConversationCreated) {
              onConversationCreated(job.id, conversationId);
            }
            onUpdateStatus(item.id, "chatConversationId", {
              chatConversationId: conversationId,
              unreadMessages: 0
            });
          }}
        />
      )}
    </>
  );
}

// ==========================================
// COMPONENTE PRINCIPAL
// ==========================================
export default function InProgressJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [evaluationTarget, setEvaluationTarget] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [showTransferSuccess, setShowTransferSuccess] = useState(false);
  const [transferData, setTransferData] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };
  const userType = localStorage.getItem("userType");
  const userId = localStorage.getItem("userId");

  //  Busca as conversas para saber o chatConversationId
  const fetchConversations = async () => {
    try {
      const conversations = await getConversations('job');
      const convMap = {};
      conversations.forEach(conv => {
        if (conv.jobId) {
          convMap[conv.jobId] = {
            id: conv.id,
            unreadCount: conv.unreadCount || 0
          };
        }
      });
      return convMap;
    } catch (error) {
      console.error('Erro ao buscar conversas:', error);
      return {};
    }
  };

  useEffect(() => {
    const fetchJobs = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }
      try {
        // Busca os jobs
        const data = userType === "Company"
          ? await getCompanyInProgressJobs(userId)
          : await getFreelancerInProgressJobs(userId);

        // Busca as conversas para associar aos jobs
        const convMap = await fetchConversations();

        // Mescla os dados
        const jobsWithChat = data.map(job => {
          const jobId = job.job?.id || job.id;
          const conv = convMap[jobId];
          return {
            ...job,
            chatConversationId: conv?.id || null,
            unreadMessages: conv?.unreadCount || 0
          };
        });

        setJobs(jobsWithChat);
      } catch (err) {
        console.error("Erro ao buscar vagas em andamento:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, [userId, userType]);

  //  Polling: sincroniza o status entre freelancer e empresa sem precisar de F5.
  // markOnTheWay/confirmArrival/earlyExit só atualizam o state de quem clicou;
  // isso aqui garante que o outro lado (empresa vendo QR aparecer, freelancer vendo
  // "finalizar turno" acontecer) recebe a atualização também.
  useEffect(() => {
    if (!userId) return;

    const pollJobs = async () => {
      try {
        const data = userType === "Company"
          ? await getCompanyInProgressJobs(userId)
          : await getFreelancerInProgressJobs(userId);

        setJobs(prevJobs =>
          data.map(freshItem => {
            const existing = prevJobs.find(j => j.id === freshItem.id);
            return {
              ...freshItem,
              chatConversationId: existing?.chatConversationId ?? null,
              unreadMessages: existing?.unreadMessages ?? 0,
            };
          })
        );
      } catch (err) {
        console.error("Erro ao atualizar vagas em andamento:", err);
      }
    };

    const interval = setInterval(pollJobs, 6000);
    return () => clearInterval(interval);
  }, [userId, userType]);

  //  SignalR para atualizar mensagens não lidas
  useEffect(() => {
    if (!userId) return;

    const socket = connectToChat(userId);

    if (socket) {
      socket.on('new_message', (messageDto) => {
        setJobs(prevJobs =>
          prevJobs.map(job => {
            const jobId = job.job?.id || job.id;

            const matchesByConversation = job.chatConversationId === messageDto.conversationId;
            const matchesByJob = !job.chatConversationId && messageDto.jobId === jobId;

            if ((matchesByConversation || matchesByJob) && messageDto.senderId !== userId) {
              return {
                ...job,
                chatConversationId: job.chatConversationId || messageDto.conversationId, // backfill pra próximas mensagens
                unreadMessages: (job.unreadMessages || 0) + 1
              };
            }

            return job;
          })
        );
      });

      socket.on('conversation_updated', (conversationId) => {
        setJobs(prevJobs =>
          prevJobs.map(job => {
            if (job.chatConversationId === conversationId) {
              return {
                ...job,
                unreadMessages: 0
              };
            }
            return job;
          })
        );
      });
    }

    return () => {
      if (socket) {
        socket.off('new_message');
        socket.off('conversation_updated');
      }
    };
  }, [userId]);

  const handleConversationCreated = (jobId, conversationId) => {
    setJobs(prevJobs =>
      prevJobs.map(job => {
        const jobIdCurrent = job.job?.id || job.id;
        if (jobIdCurrent === jobId) {
          return {
            ...job,
            chatConversationId: conversationId,
            unreadMessages: 0
          };
        }
        return job;
      })
    );
  };

  const handleEarlyExit = async (
    item,
    { finishedOnTime, reason, freelancerPayment }
  ) => {
    try {
      await earlyExitJob(item.id, {
        finishedOnTime,
        reason,
        freelancerPayment,
      });

      setJobs((prev) => prev.filter((job) => job.id !== item.id));

      setTransferData({
        id: item.id,
        jobTitle: item.job?.title || "Vaga finalizada",
        amount: freelancerPayment ?? item.job?.payment ?? 0,
        freelancerName: item.freelancer?.name || "Freelancer",
        freelancerId: item.freelancer?.id,
        freelancerAvatar: item.freelancer?.avatarUrl,
      });

      setShowTransferSuccess(true);
    } catch (error) {
      showToast(
        error.response?.data?.message ||
        "Não foi possível finalizar a vaga. Verifique os dados e tente novamente.",
        "error"
      );
    }
  };

  const handleContinueToEvaluation = () => {
    setShowTransferSuccess(false);

    if (transferData?.id) {
      setEvaluationTarget({
        id: transferData.id,
        job: { title: transferData.jobTitle },
        freelancer: {
          id: transferData.freelancerId,
          name: transferData.freelancerName,
          avatarUrl: transferData.freelancerAvatar,
        },
      });
    }
  };

  const handleUpdateStatus = (applicationId, field, extraData = {}) => {
    setJobs((prevJobs) =>
      prevJobs.map((job) =>
        job.id === applicationId
          ? {
            ...job,
            [field]: true,
            unreadMessages: extraData.unreadMessages !== undefined
              ? extraData.unreadMessages
              : job.unreadMessages || 0,
            ...extraData
          }
          : job
      )
    );
  };

  const handleExtraHoursAdded = (applicationId, newEndTime) => {
    setJobs((prevJobs) =>
      prevJobs.map((item) =>
        item.id === applicationId
          ? { ...item, job: { ...item.job, endTime: newEndTime } }
          : item
      )
    );
  };

  if (loading) {
    return (
      <div className="ip-loading">
        <Clock size={24} className="ip-loading__icon" />
        Carregando vagas em andamento...
      </div>
    );
  }

  const job = evaluationTarget?.job || evaluationTarget;
  const freelancer = evaluationTarget?.freelancer || evaluationTarget?.user;
  const targetName =
    userType === "Company"
      ? freelancer?.nomeSocial?.trim() || freelancer?.name
      : job?.companyName;
  const targetAvatar = userType === "Company" ? freelancer?.avatarUrl : job?.companyAvatar;

  return (
    <>
      {jobs.length === 0 ? (
        <EmptyState message="Nenhum evento está em andamento." />
      ) : (
        <div className="ip-wrapper">
          <div className="ip-header">
            <h2 className="ip-title">Em Andamento</h2>
            <span className="ip-count">
              {jobs.length} vaga{jobs.length !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="ip-grid">
            {jobs.map((item) => (
              <InProgressCard
                key={item.id}
                item={item}
                userType={userType}
                onEarlyExit={handleEarlyExit}
                onUpdateStatus={handleUpdateStatus}
                onExtraHoursAdded={handleExtraHoursAdded}
                onConversationCreated={handleConversationCreated}
                showToast={showToast}
              />
            ))}
          </div>
        </div>
      )}

      <TransferSuccessModal
        isOpen={showTransferSuccess}
        onClose={() => {
          setShowTransferSuccess(false);
        }}
        onContinue={handleContinueToEvaluation}
        jobTitle={transferData?.jobTitle}
        amount={transferData?.amount}
        freelancerName={transferData?.freelancerName}
      />
      {evaluationTarget && (
        <EvaluationModal
          jobApplicationId={evaluationTarget.id}
          evaluatorId={userId}
          freelancerId={evaluationTarget.freelancer?.id}
          targetName={evaluationTarget.freelancer?.name || transferData?.freelancerName}
          targetAvatar={evaluationTarget.freelancer?.avatarUrl}
          onClose={() => setEvaluationTarget(null)}
          onSubmitted={() => setEvaluationTarget(null)}
        />
      )}

      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: "", type: "success" })}
      />
    </>
  );
}